import type {
  AgentChatMessage,
  AgentConversationSummary,
} from '@/services/agents/config';
import {
  contextSwitchFromMetadata,
  contextSwitchLabel,
} from '@/components/agent-chat/context-switch';
import { downloadBlob } from './download-blob';

const ROLE_LABELS: Record<AgentChatMessage['role'], string> = {
  user: 'You',
  assistant: 'Assistant',
  system: 'System',
};

/**
 * Build a Markdown transcript for a conversation. Pure and unit-tested;
 * the download side effects live in `downloadConversationMarkdown`.
 */
export function buildConversationMarkdown(
  conversation: Pick<
    AgentConversationSummary,
    'title' | 'preview' | 'created_at' | 'last_message_at'
  >,
  messages: AgentChatMessage[],
  options: { agentName?: string } = {},
): string {
  const title = conversation.title || conversation.preview || 'Conversation';
  const lines: string[] = [`# ${title}`, ''];
  if (options.agentName) lines.push(`Agent: ${options.agentName}`);
  if (conversation.created_at) {
    lines.push(
      `Started: ${new Date(conversation.created_at).toLocaleString()}`,
    );
  }
  if (conversation.last_message_at) {
    lines.push(
      `Last message: ${new Date(conversation.last_message_at).toLocaleString()}`,
    );
  }
  lines.push('', '---', '');

  for (const message of messages) {
    // A context switch is not a turn -- it reads as a rule across the
    // transcript, the way the chat itself renders it, rather than as
    // something the System said.
    const context = contextSwitchFromMetadata(message);
    if (context) {
      lines.push('---', '', `_${contextSwitchLabel(context)}_`, '');
      continue;
    }
    const label = ROLE_LABELS[message.role] ?? message.role;
    const stamp = message.created_at
      ? ` — ${new Date(message.created_at).toLocaleString()}`
      : '';
    lines.push(`## ${label}${stamp}`, '', message.content.trim(), '');
  }
  return lines.join('\n').trimEnd() + '\n';
}

/** Filesystem-safe `.md` filename derived from the conversation label. */
export function conversationExportFilename(
  label: string,
  now: Date = new Date(),
): string {
  const slug =
    label
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 60) || 'conversation';
  const date = now.toISOString().slice(0, 10);
  return `chat-${slug}-${date}.md`;
}

/**
 * Everything the export needs about a conversation. Narrower than
 * `AgentConversationSummary` so callers that only hold a partial record --
 * a chat opened by id that isn't in the caller's own list -- can export it
 * without inventing fields, and so the ones they cannot supply are visible
 * here rather than standing in as empty strings.
 */
export type ExportableConversation = Pick<
  AgentConversationSummary,
  'title' | 'preview' | 'created_at' | 'last_message_at'
>;

export function downloadConversationMarkdown(
  conversation: ExportableConversation,
  messages: AgentChatMessage[],
  options: { agentName?: string } = {},
): void {
  const markdown = buildConversationMarkdown(conversation, messages, options);
  downloadBlob(
    new Blob([markdown], { type: 'text/markdown;charset=utf-8;' }),
    conversationExportFilename(
      conversation.title || conversation.preview || 'conversation',
    ),
  );
}
